/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect } from "react"
import { Dialog, DialogContent, DialogActions } from "@mui/material"
import CloseIcon from '@mui/icons-material/Close'
import {
  StyledModalTitleContainer,
  StyledModalTitle,
  SectionTitle,
  RecordCard,
  AvatarCircle,
  RecordNameRow,
  RecordName,
  RecordSubText,
  MatchCard,
  MatchDetailText,
  StyledMergeButton,
  ConflictBadge,
  StyledMatchRadio,
  LoaderContainer,
} from "./styles"
import { ModalCancelButton } from "../modal_cancel_button/index"
import { startCase } from 'lodash'
import { Loader } from "../loader"
import { LoadingSpinner } from "../loading_spinner"

const formatConflictTag = (tag) => {
  if (!tag) return '-'

  return startCase(tag)
}

const getInitials = (firstName, lastName) => {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase()
}

export const MergeEmployeeModal = ({
  open,
  handleClose,
  data,
  selectedMatch,
  setSelectedMatch,
  isLoading,
  handleMerge,
  isMerging,
}) => {

  useEffect(() => {
    if (data?.candidates_list?.length === 1) setSelectedMatch(data.candidates_list[0])
  }, [data])

  const conflictedName = `${data?.first_name || ''} ${data?.last_name || ''}`.trim()

  return (
    <Dialog
      data-testid='merge-employee-modal'
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='sm'
      PaperProps={{
        sx: {
          minHeight: '300px',
        },
      }}
    >
      {
        isLoading ? (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        ) : (
          <React.Fragment>
            <DialogContent sx={{ padding: '10px 20px' }}>
              <StyledModalTitleContainer>
                <StyledModalTitle>
                  Merge record
                </StyledModalTitle>
                <CloseIcon
                  data-testid="cross-icon"
                  onClick={handleClose}
                  sx={{ cursor: 'pointer', color: 'grey' }}
                />
              </StyledModalTitleContainer>

              <SectionTitle>
                Conflicted data
              </SectionTitle>

              <RecordCard>
                <AvatarCircle>
                  {getInitials(data?.first_name, data?.last_name)}
                </AvatarCircle>
                <div>
                  <RecordNameRow>
                    <RecordName>{conflictedName}</RecordName>
                    <ConflictBadge label={formatConflictTag(data?.conflict_tag)} />
                  </RecordNameRow>
                  <RecordSubText>
                    ID {data?.id}{data?.conflict_phone ? ` · ${data.conflict_phone}` : ''}
                  </RecordSubText>
                </div>
              </RecordCard>

              <SectionTitle>
                Found matches
              </SectionTitle>

              {
                data?.candidates_list?.map((candidate) => (
                  <MatchCard
                    key={candidate.id}
                    selected={selectedMatch?.id === candidate.id}
                    onClick={() => setSelectedMatch(candidate)}
                    data-testid='possible-match-card'
                  >
                    <StyledMatchRadio
                      data-testid='possible-match-radio'
                      checked={selectedMatch?.id === candidate.id}
                      onChange={() => setSelectedMatch(candidate)}
                      disableRipple
                      size="small"
                    />
                    <div>
                      <RecordName>
                        {`${candidate?.first_name || ''} ${candidate?.last_name || ''}`.trim()}
                      </RecordName>
                      <MatchDetailText>ID {candidate?.id}</MatchDetailText>
                      {candidate?.email && <MatchDetailText>{candidate.email}</MatchDetailText>}
                      {candidate?.phone_number && <MatchDetailText>{candidate.phone_number}</MatchDetailText>}
                    </div>
                  </MatchCard>
                ))
              }
            </DialogContent>

            <DialogActions sx={{ padding: '20px' }}>
              <ModalCancelButton
                data-testid='close-modal'
                onClick={handleClose}
                disabled={isMerging}
              >
                Cancel
              </ModalCancelButton>
              <StyledMergeButton
                data-testid='merge-record-btn'
                variant="contained"
                onClick={handleMerge}
                disabled={!selectedMatch || isMerging}
              >
                {isMerging ? <LoadingSpinner isLoading={true} /> : 'Merge'}
              </StyledMergeButton>
            </DialogActions>
          </React.Fragment>
        )
      }
    </Dialog>
  )
}
